/**
 * API back-office : messagerie parent ↔ école + modération commentaires.
 * Préfixé /portail-parent/admin/...
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

export interface MessageParentItem {
  id: number;
  thread_id: number;
  parent_id: number;
  parent_nom: string;
  parent_prenoms: string;
  parent_telephone: string | null;
  parent_email: string | null;
  matricule_etudiant: string;
  destinataire_type: 'scolarite' | 'surveillant' | 'formateur';
  destinataire_formateur_nom: string;
  matiere_libelle: string;
  sujet: string;
  contenu: string;
  auteur_type: 'parent' | 'ecole';
  auteur_nom_affiche: string;
  lu: boolean;
  date_envoi: string;
  archive: boolean;
  extrait_dernier?: string;
  dernier_date?: string;
  dernier_auteur?: 'parent' | 'ecole';
  nb_non_lus?: number;
}

export interface MessagesParentsResponse {
  results: MessageParentItem[];
  total: number;
  nb_total_non_lus: number;
  roles_visibles: string[];
}

export interface CommentaireParentItem {
  id: number;
  actualite_id: number;
  parent_id: number;
  parent_nom: string;
  parent_prenoms: string;
  parent_telephone: string | null;
  parent_email: string | null;
  contenu: string;
  date_creation: string;
  supprime: boolean;
  raison_suppression?: string;
  supprime_par_nom?: string;
  date_suppression?: string | null;
}

export const messagesParentsApi = createApi({
  reducerPath: 'messagesParentsApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['MessagesParents', 'CommentairesParents'],
  endpoints: (builder) => ({
    getMessagesParents: builder.query<MessagesParentsResponse, {
      statut?: 'tous' | 'non_lus' | 'archives';
      type?: 'scolarite' | 'surveillant' | 'formateur';
      q?: string;
    } | void>({
      query: (params) => ({
        url: '/portail-parent/admin/messages/',
        params: params ?? undefined,
      }),
      providesTags: ['MessagesParents'],
    }),

    getThreadParent: builder.query<{
      thread_id: number;
      racine: MessageParentItem;
      enfant: { matricule: string; nom: string; prenoms: string; classe: string };
      messages: MessageParentItem[];
    }, number>({
      query: (id) => `/portail-parent/admin/messages/${id}/`,
      providesTags: (_r, _e, id) => [{ type: 'MessagesParents', id }],
    }),

    repondreThreadParent: builder.mutation<{ success: boolean }, { id: number; contenu: string }>({
      query: ({ id, contenu }) => ({
        url: `/portail-parent/admin/messages/${id}/`,
        method: 'POST',
        body: { contenu },
      }),
      invalidatesTags: ['MessagesParents'],
    }),

    archiverThreadParent: builder.mutation<{ success: boolean }, { id: number; archive: boolean }>({
      query: ({ id, archive }) => ({
        url: `/portail-parent/admin/messages/${id}/archiver/`,
        method: 'POST',
        body: { archive },
      }),
      invalidatesTags: ['MessagesParents'],
    }),

    getCommentairesParents: builder.query<{ results: CommentaireParentItem[]; total: number }, {
      actualite_id?: number; supprime?: boolean;
    } | void>({
      query: (params) => ({
        url: '/portail-parent/admin/commentaires/',
        params: params ?? undefined,
      }),
      providesTags: ['CommentairesParents'],
    }),

    modererCommentaireParent: builder.mutation<{ success: boolean }, { id: number; action: 'supprimer' | 'restaurer'; raison?: string }>({
      query: ({ id, action, raison }) => ({
        url: `/portail-parent/admin/commentaires/${id}/moderer/`,
        method: 'POST',
        body: action === 'supprimer' ? { action, raison } : { action },
      }),
      invalidatesTags: ['CommentairesParents'],
    }),
  }),
});

export const {
  useGetMessagesParentsQuery,
  useGetThreadParentQuery,
  useRepondreThreadParentMutation,
  useArchiverThreadParentMutation,
  useGetCommentairesParentsQuery,
  useModererCommentaireParentMutation,
} = messagesParentsApi;
